import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Header from './Header';
import { useUser } from '../contexts/UserContext';
import { BLOG_CATEGORIES } from './BlogIndex';
import './BlogEditor.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

function slugifyClient(s) {
  return (s || '').toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 200);
}

const previewComponents = {
  code({ inline, className, children }) {
    const match = /language-(\w+)/.exec(className || '');
    const value = String(children ?? '');
    if (!inline && (match || value.includes('\n'))) {
      return (
        <div className="terview-code-block">
          <div className="terview-code-header">
            <span className="terview-code-lang">{match ? match[1] : 'code'}</span>
          </div>
          <SyntaxHighlighter
            language={(match && match[1]) || 'text'}
            style={oneDark}
            showLineNumbers
            wrapLongLines
            customStyle={{ margin: 0, padding: '14px 16px', background: '#0d1117', fontSize: '0.88em' }}
          >
            {value.replace(/\n$/, '')}
          </SyntaxHighlighter>
        </div>
      );
    }
    return <code className="terview-inline-code">{children}</code>;
  },
};

export default function BlogEditor({ mode = 'new' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();

  const [step, setStep] = useState('compose'); // 'compose' | 'verify'
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [postId, setPostId] = useState(null);
  const [postStatus, setPostStatus] = useState('draft'); // current saved status
  const [savedSlug, setSavedSlug] = useState('');

  const [form, setForm] = useState({
    title: '',
    summary: '',
    category: BLOG_CATEGORIES[1], // first real category, not "All"
    hero_image_url: '',
    slug: '',
    content_md: '',
  });
  // Track whether the user has manually edited the slug — if not, we keep
  // it auto-synced to the title.
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // ── Admin gate ──────────────────────────────────────────────────────
  // The server returns 403 anyway, but we also render a clear client-side
  // message so non-admins don't see a half-rendered editor. Stored as a
  // ReactNode that the bottom of the function returns instead of the editor
  // — defined here (not via early-return) so subsequent hooks always run
  // in the same order, satisfying React's Rules of Hooks.
  let accessDenied = null;
  if (!userLoading && !user) {
    accessDenied = (
      <div className="blog-editor-page">
        <Header />
        <div className="blog-editor-gate">
          <h2>Sign in required</h2>
          <p>You need an admin account to publish posts.</p>
          <Link to="/" className="blog-editor-back">← Back to home</Link>
        </div>
      </div>
    );
  } else if (!userLoading && user && !user.is_admin) {
    accessDenied = (
      <div className="blog-editor-page">
        <Header />
        <div className="blog-editor-gate">
          <h2>Admin access required</h2>
          <p>Only Terview admins can create or edit blog posts.</p>
          <Link to="/blog" className="blog-editor-back">← Back to blog</Link>
        </div>
      </div>
    );
  }

  // Load existing post for edit mode.
  useEffect(() => {
    if (mode !== 'edit' || !id || !user?.is_admin) return;
    let cancelled = false;
    setLoading(true);
    setErrorMsg('');
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/blog/admin/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Failed to load');
        return r.json();
      })
      .then(({ post }) => {
        if (cancelled) return;
        setPostId(post.id);
        setPostStatus(post.status);
        setSavedSlug(post.slug);
        setForm({
          title: post.title || '',
          summary: post.summary || '',
          category: post.category || BLOG_CATEGORIES[1],
          hero_image_url: post.hero_image_url || '',
          slug: post.slug || '',
          content_md: post.content_md || '',
        });
        setSlugManuallyEdited(true);
      })
      .catch(e => !cancelled && setErrorMsg(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [mode, id, user]);

  // Auto-update slug from title until the user manually edits it.
  useEffect(() => {
    if (slugManuallyEdited) return;
    setForm(f => ({ ...f, slug: slugifyClient(f.title) }));
  }, [form.title, slugManuallyEdited]);

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSlugChange = (v) => {
    setSlugManuallyEdited(true);
    setForm(f => ({ ...f, slug: slugifyClient(v) }));
  };

  // Estimated read time / word count for the verify step.
  const stats = useMemo(() => {
    const md = form.content_md || '';
    const words = md.replace(/```[\s\S]*?```/g, ' ').split(/\s+/).filter(Boolean).length;
    return { words, minutes: Math.max(1, Math.round(words / 200)) };
  }, [form.content_md]);

  // ── AI generation: from a title, fill category + summary + content. ───
  // The server endpoint returns structured JSON; we paste each field into
  // the form so the user can still tweak anything before publishing.
  async function handleGenerate() {
    const t = form.title.trim();
    if (!t) {
      setErrorMsg('Type a title first — that\'s all the AI needs to write the rest.');
      return;
    }
    setGenerating(true);
    setErrorMsg('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE_URL}/api/blog/admin/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: t }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.details || data.error || `HTTP ${r.status}`);
      }
      const { category, summary, content_md, hero_image_url } = await r.json();
      setForm(f => ({
        ...f,
        category: category || f.category,
        summary: summary || f.summary,
        content_md: content_md || f.content_md,
        // Only overwrite an existing hero if the API returned a new one — so
        // a manually-pasted URL isn't wiped if image generation fails server-side.
        hero_image_url: hero_image_url || f.hero_image_url,
      }));
    } catch (e) {
      setErrorMsg(`Generation failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  }

  // Save as draft. Returns the saved id (creating if needed). The verify
  // step calls this first so "Post" only needs to flip status — no risk of
  // losing edits made on the preview screen.
  async function saveDraft() {
    setSaving(true);
    setErrorMsg('');
    const token = localStorage.getItem('token');
    try {
      if (postId) {
        const r = await fetch(`${API_BASE_URL}/api/blog/${postId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(form),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Failed to save');
        return postId;
      } else {
        const r = await fetch(`${API_BASE_URL}/api/blog`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(form),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Failed to create');
        const { id: newId, slug: newSlug } = await r.json();
        setPostId(newId);
        setSavedSlug(newSlug);
        return newId;
      }
    } finally {
      setSaving(false);
    }
  }

  const goVerify = async () => {
    if (!form.title.trim() || !form.content_md.trim() || !form.category) {
      setErrorMsg('Title, category and content are required.');
      return;
    }
    try {
      await saveDraft();
      setStep('verify');
      window.scrollTo({ top: 0 });
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setErrorMsg('');
    try {
      // Ensure latest edits are saved before flipping status.
      const idToPublish = await saveDraft();
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE_URL}/api/blog/${idToPublish}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'published' }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Failed to publish');
      navigate(`/blog/${form.slug || savedSlug}`);
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!postId) return;
    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/blog/${postId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'draft' }),
      });
      setPostStatus('draft');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!postId) return;
    if (!window.confirm('Delete this post permanently? This cannot be undone.')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_BASE_URL}/api/blog/${postId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    navigate('/blog');
  };

  // All hooks have run by this point — safe to branch on access / loading.
  if (accessDenied) return accessDenied;

  if (loading) {
    return (
      <div className="blog-editor-page">
        <Header />
        <div className="blog-editor-gate">Loading post…</div>
      </div>
    );
  }

  return (
    <div className="blog-editor-page">
      <Header />

      <div className="blog-editor-container">
        <div className="blog-editor-topbar">
          <div className="blog-editor-crumbs">
            <Link to="/blog">Blog</Link>
            <span>›</span>
            <span>{mode === 'edit' ? 'Edit post' : 'New post'}</span>
            {postStatus === 'published' && <span className="blog-editor-status pub">Published</span>}
            {postStatus === 'draft' && postId && <span className="blog-editor-status draft">Draft</span>}
          </div>
          <div className="blog-editor-step-pills">
            <span className={`pill ${step === 'compose' ? 'active' : ''}`}>1. Compose</span>
            <span className={`pill ${step === 'verify' ? 'active' : ''}`}>2. Verify &amp; post</span>
          </div>
        </div>

        {errorMsg && <div className="blog-editor-error">{errorMsg}</div>}

        {step === 'compose' && (
          <div className="blog-editor-compose">
            <div className="blog-editor-left">
              <div className="be-ai-panel">
                <div className="be-ai-panel-head">
                  <span className="be-ai-spark" aria-hidden="true">✨</span>
                  <div>
                    <div className="be-ai-title">Just type a title — AI writes the rest</div>
                    <div className="be-ai-sub">
                      Category, summary, and the full markdown article are auto-generated.
                      You can still edit anything before publishing.
                    </div>
                  </div>
                </div>
                <div className="be-ai-row">
                  <input
                    className="be-input be-ai-input"
                    value={form.title}
                    onChange={e => handleChange('title', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !generating && form.title.trim()) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }}
                    placeholder="e.g. How to Crush a System Design Interview in 2026"
                    disabled={generating}
                  />
                  <button
                    type="button"
                    className="be-btn be-ai-btn"
                    onClick={handleGenerate}
                    disabled={generating || !form.title.trim()}
                  >
                    {generating ? (
                      <><span className="be-ai-spinner" /> Generating…</>
                    ) : (
                      <>✨ Generate with AI</>
                    )}
                  </button>
                </div>
                {generating && (
                  <div className="be-ai-progress">
                    Writing your article and generating a hero image — typically
                    20-40 seconds. Don't refresh.
                  </div>
                )}
              </div>


              <label>Slug (URL)</label>
              <div className="be-slug">
                <span className="be-slug-prefix">/blog/</span>
                <input
                  className="be-input"
                  value={form.slug}
                  onChange={e => handleSlugChange(e.target.value)}
                  placeholder="auto-from-title"
                />
              </div>

              <label>Category</label>
              <select
                className="be-input"
                value={form.category}
                onChange={e => handleChange('category', e.target.value)}
              >
                {BLOG_CATEGORIES.filter(c => c !== 'All').map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <label>Hero image URL <span className="be-optional">(optional)</span></label>
              <input
                className="be-input"
                value={form.hero_image_url}
                onChange={e => handleChange('hero_image_url', e.target.value)}
                placeholder="https://…"
              />

              <label>Summary <span className="be-optional">(1–2 sentences, shown on the index page)</span></label>
              <textarea
                className="be-input be-textarea"
                rows={3}
                value={form.summary}
                onChange={e => handleChange('summary', e.target.value)}
                placeholder="A short hook that explains what readers will learn."
              />

              <label>Content (Markdown)</label>
              <textarea
                className="be-input be-content"
                value={form.content_md}
                onChange={e => handleChange('content_md', e.target.value)}
                placeholder={`# Heading\n\nYour article body. Use **markdown** — H2 headings auto-populate the table of contents.\n\n\`\`\`js\nconsole.log('Code blocks render with line numbers');\n\`\`\``}
              />
            </div>

            <div className="blog-editor-right">
              <div className="be-side-card">
                <h4>Live preview</h4>
                <div className="be-side-preview">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={previewComponents}>
                    {form.content_md || '_Nothing to preview yet._'}
                  </ReactMarkdown>
                </div>

                <div className="be-side-actions">
                  <button
                    type="button"
                    className="be-btn be-btn-secondary"
                    onClick={saveDraft}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save draft'}
                  </button>
                  <button
                    type="button"
                    className="be-btn be-btn-primary"
                    onClick={goVerify}
                    disabled={saving}
                  >
                    Verify &amp; review →
                  </button>
                  {postId && (
                    <button
                      type="button"
                      className="be-btn be-btn-danger-link"
                      onClick={handleDelete}
                    >
                      Delete post
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <div className="blog-editor-verify">
            <div className="be-verify-head">
              <h3>Review before posting</h3>
              <p>
                You're about to publish <strong>"{form.title}"</strong> at
                <code> /blog/{form.slug}</code>.
                {' '}This will make it visible to every visitor.
              </p>
              <div className="be-verify-stats">
                <span>📝 {stats.words} words</span>
                <span>⏱ ~{stats.minutes} min read</span>
                <span>🏷 {form.category}</span>
              </div>
            </div>

            <div className="be-verify-preview">
              {form.hero_image_url && (
                <img src={form.hero_image_url} alt="" className="be-verify-hero" />
              )}
              <h1>{form.title}</h1>
              {form.summary && <p className="be-verify-summary">{form.summary}</p>}
              <hr />
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={previewComponents}>
                {form.content_md}
              </ReactMarkdown>
            </div>

            <div className="be-verify-actions">
              <button
                type="button"
                className="be-btn be-btn-secondary"
                onClick={() => setStep('compose')}
              >
                ← Back to edit
              </button>
              {postStatus === 'published' ? (
                <button
                  type="button"
                  className="be-btn be-btn-warning"
                  onClick={handleUnpublish}
                  disabled={publishing}
                >
                  {publishing ? 'Updating…' : 'Unpublish'}
                </button>
              ) : null}
              <button
                type="button"
                className="be-btn be-btn-primary be-btn-large"
                onClick={handlePublish}
                disabled={publishing}
              >
                {publishing ? 'Publishing…' : postStatus === 'published' ? 'Save & republish' : 'Post'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
